import type { ContextSessionResponse } from "@kontextwerk/control-web-client"
import type { PermissionState } from "@kontextwerk/control-web-frame/shared/logic/authorization"
import { captureMembershipAccess } from "../../../../../api/kehrblatt/membership-access"
import { MemberAccess } from "../_shared/_logic/member-access"
import type { UserMember } from "../_shared/_logic/user-types"
import { resolveOwnMembership } from "./member"

/**
 * Identity of one access controller: confirmed context pair, tenant and membership. A record of
 * another tenant than the confirmed context never gets one.
 */
export const memberAccessKey = (
  session: ContextSessionResponse | null,
  member: UserMember | null,
  organizationId: string | null,
): string | null => {
  const context = session?.context

  if (
    !session ||
    context?.kind !== "organization" ||
    !member ||
    !organizationId ||
    context.organization_id.toLowerCase() !== organizationId.toLowerCase()
  )
    return null

  return [
    session.session_id,
    session.context_revision,
    context.organization_id,
    member.membershipId,
  ].join(":")
}

/** Binds a controller to the captured KB context; null while no matching context is confirmed. */
export const bindMemberAccess = (
  session: ContextSessionResponse | null,
  permissions: PermissionState,
  member: UserMember | null,
): MemberAccess | null => {
  const context = session?.context

  if (!session || context?.kind !== "organization" || !member) return null

  try {
    const binding = captureMembershipAccess(context.organization_id)

    return new MemberAccess({
      membershipId: member.membershipId,
      port: binding.api,
      capabilities: {
        viewMembership: permissions.allows("core.memberships", "view"),
        manageMembership: permissions.allows("core.memberships", "manage"),
        viewRoles: permissions.allows("core.roles", "view"),
      },
      own: resolveOwnMembership(session),
      actorRoleId: context.role_id,
      delegated: Boolean(context.delegation),
      signal: binding.signal,
    })
  } catch {
    return null
  }
}

const pathOf = (target: string) => target.split(/[?#]/)[0].replace(/\/+$/, "")

/** Pages and tabs below the member record keep its drafts; any other target leaves the record. */
export const isWithinMemberRecord = (target: string, recordPath: string | null | undefined) => {
  const base = recordPath ? pathOf(recordPath) : null
  const path = pathOf(target)

  return Boolean(base && (path === base || path.startsWith(`${base}/`)))
}
